/* Merchant-owned coordination for the four native free-DCTL flyouts.
 * Native Omnisend still owns eligibility, experiment assignment and submission.
 * No cookies, consent values, credentials or field values are persisted here.
 */
const FORM_IDS = [
  '6ad41222e171c7a9af2d14b1',
  '6ad412d6e171c7a9af2d14b3',
  '6ad412d6e171c7a9af2d14b4',
  '6ad4122be98db6853821f545',
];
const MOBILE_ID = FORM_IDS[3];
const PREFERENCES = [
  '.Avada-CookieBar__PreferencePopup',
  '.Avada-CookiesBar-Preferences__PopUp',
  '.Avada-CookiesBar__PreferencesCard',
].join(',');
const CONSENT_PANELS = `#Avada-CookiesBar .Avada-CookiesBar_Container,${PREFERENCES}`;

export function isRendered(element, win) {
  if (!element?.isConnected || !element.getClientRects().length) return false;
  for (let node = element; node; node = node.parentElement) {
    const style = win.getComputedStyle(node);
    if (node.hasAttribute('hidden') || node.hasAttribute('inert') || node.getAttribute('aria-hidden') === 'true' ||
        style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' ||
        Number(style.opacity) === 0) return false;
  }
  const rect = element.getBoundingClientRect();
  const viewport = win.visualViewport;
  const left = viewport?.offsetLeft || 0;
  const top = viewport?.offsetTop || 0;
  return rect.width > 0 && rect.height > 0 && rect.right > left && rect.bottom > top &&
    rect.left < left + (viewport?.width || win.innerWidth) && rect.top < top + (viewport?.height || win.innerHeight);
}

function ownStyle(element, property, value, priority = 'important') {
  let before = element.style.getPropertyValue(property);
  let beforePriority = element.style.getPropertyPriority(property);
  const enforce = () => {
    const current = element.style.getPropertyValue(property);
    const currentPriority = element.style.getPropertyPriority(property);
    if (current === value && currentPriority === priority) return;
    before = current;
    beforePriority = currentPriority;
    element.style.setProperty(property, value, priority);
  };
  const release = () => {
    if (element.style.getPropertyValue(property) !== value || element.style.getPropertyPriority(property) !== priority) return;
    if (before) element.style.setProperty(property, before, beforePriority);
    else element.style.removeProperty(property);
  };
  enforce();
  release.enforce = enforce;
  return release;
}

function ownAttribute(element, name, value) {
  const before = element.getAttribute(name);
  element.setAttribute(name, value);
  return () => {
    if (element.getAttribute(name) !== value) return;
    if (before === null) element.removeAttribute(name);
    else element.setAttribute(name, before);
  };
}

export function suspendPanel(element) {
  const properties = [['visibility', 'hidden'], ['pointer-events', 'none']].map(([name, value]) => ({
    name, value, before: element.style.getPropertyValue(name), priority: element.style.getPropertyPriority(name),
  }));
  const attributes = [['inert', ''], ['aria-hidden', 'true']].map(([name, value]) => ({
    name, value, before: element.getAttribute(name),
  }));
  let released = false;
  const enforce = () => {
    if (released) return;
    for (const property of properties) {
      const value = element.style.getPropertyValue(property.name);
      const priority = element.style.getPropertyPriority(property.name);
      if (value === property.value && priority === 'important') continue;
      property.before = value;
      property.priority = priority;
      element.style.setProperty(property.name, property.value, 'important');
    }
    for (const attribute of attributes) {
      const value = element.getAttribute(attribute.name);
      if (value === attribute.value) continue;
      attribute.before = value;
      element.setAttribute(attribute.name, attribute.value);
    }
  };
  const release = () => {
    if (released) return;
    released = true;
    for (const property of properties) {
      if (element.style.getPropertyValue(property.name) !== property.value ||
          element.style.getPropertyPriority(property.name) !== 'important') continue;
      if (property.before) element.style.setProperty(property.name, property.before, property.priority);
      else element.style.removeProperty(property.name);
    }
    for (const attribute of attributes) {
      if (element.getAttribute(attribute.name) !== attribute.value) continue;
      if (attribute.before === null) element.removeAttribute(attribute.name);
      else element.setAttribute(attribute.name, attribute.before);
    }
  };
  enforce();
  release.enforce = enforce;
  return release;
}

// Google renders paired a-<widget> and c-<widget> iframe names. Fail closed
// if that observed native structure changes; never promote an unknown iframe.
function googleFrame(frame, kind) {
  try {
    const url = new URL(frame.src);
    return ['https://www.google.com', 'https://www.recaptcha.net'].includes(url.origin) &&
      url.pathname === `/recaptcha/api2/${kind}`;
  } catch { return false; }
}

export function ownedChallenge(formID, doc, win) {
  const container = doc.getElementById(`recaptcha-container-${formID}`);
  if (!container?.isConnected) return;
  const anchorNames = new Set(Array.from(container.querySelectorAll('iframe[src*="/recaptcha/"][src*="/anchor"]'))
    .filter(frame => googleFrame(frame, 'anchor') && /^a-[\w-]+$/.test(frame.name)).map(frame => frame.name.slice(2)));
  return Array.from(doc.querySelectorAll('iframe[src*="/recaptcha/"][src*="/bframe"]')).find(frame => {
    if (!googleFrame(frame, 'bframe') || !/^c-[\w-]+$/.test(frame.name) || !anchorNames.has(frame.name.slice(2)) || !isRendered(frame, win)) return false;
    const layer = frame.parentElement?.parentElement;
    return frame.parentElement?.tagName === 'DIV' && layer?.parentElement === doc.body &&
      layer.tagName === 'DIV' && !layer.id &&
      layer.querySelectorAll('iframe').length === 1;
  });
}

export function validateSubmission(controls, verificationReady) {
  for (const field of controls) {
    if (field.disabled) continue;
    if (field.name === 'firstName' && !field.value.trim()) return { kind: 'blank-name', field };
    if (!field.checkValidity()) {
      field.reportValidity();
      return { kind: 'invalid', field };
    }
  }
  return { kind: verificationReady ? 'ready' : 'verification-unavailable' };
}

export function startPopupGuard(doc = document, win = window) {
  const mounted = new Map();
  const notices = new Set();
  let stopped = false;
  let frame = 0;
  let focusedField = null;
  let consentPanel = null;
  let restoreConsentTabindex = null;
  let challengeState = null;
  let submitting = null;

  const forms = () => FORM_IDS.flatMap(id => {
    const root = doc.getElementById(`omnisend-form-${id}`);
    const panel = root?.querySelector(`.omnisend-form-${id}-container`);
    return panel ? [{ id, panel }] : [];
  });
  const focusStillAvailable = element => element?.isConnected && !element.disabled && isRendered(element, win);

  function releasePanel(panel, state) {
    state.suspended?.();
    state.restore.reverse().forEach(undo => undo());
    for (const [property, value] of state.variables) {
      if (panel.style.getPropertyValue(property) !== value.owned) continue;
      if (value.before) panel.style.setProperty(property, value.before, value.priority);
      else panel.style.removeProperty(property);
    }
  }

  function sync() {
    frame = 0;
    if (stopped) return;
    const current = forms();
    const active = new Set(current.map(({ panel }) => panel));
    for (const [panel, state] of mounted) {
      if (!active.has(panel)) {
        releasePanel(panel, state);
        mounted.delete(panel);
      }
    }
    const surfaces = Array.from(doc.querySelectorAll(CONSENT_PANELS)).filter(panel => isRendered(panel, win));
    const consent = surfaces.find(panel => panel.matches(PREFERENCES)) || surfaces[0];
    if (submitting && !active.has(submitting.panel)) submitting = null;
    const submissionVisible = submitting && submitting.panel.getClientRects().length &&
      win.getComputedStyle(submitting.panel).display !== 'none';
    const challenge = submissionVisible && ownedChallenge(submitting.id, doc, win);
    // A Google challenge is already a separate modal. Keep it ahead of the cookie
    // layer until it closes; never hide, solve or otherwise manipulate its iframe.
    if (challengeState && (challengeState.challenge !== challenge || challengeState.consent !== consent)) {
      challengeState.release();
      challengeState = null;
    }
    if (consent && challenge && !challengeState) {
      const layer = challenge.parentElement.parentElement;
      const cookieRoot = doc.getElementById('Avada-CookiesBar');
      if (layer && layer.id !== 'omnisend-forms-wrapper' && cookieRoot) {
        const undo = [ownStyle(layer, 'z-index', '2147483647'), ownStyle(cookieRoot, 'z-index', '2147483646')];
        challengeState = { challenge, consent, enforce: () => undo.forEach(restore => restore.enforce()),
          release: () => undo.reverse().forEach(restore => restore()) };
      }
    }
    challengeState?.enforce();
    const suspend = Boolean(consent && !challengeState);
    for (const { id, panel } of current) {
      let state = mounted.get(panel);
      if (!state) {
        const bottom = Number.parseFloat(win.getComputedStyle(panel).bottom) || 0;
        state = { bottom, suspended: null, restore: [ownAttribute(panel, 'aria-modal', 'false')], variables: new Map() };
        panel.classList.add('pt-popup-guard-panel');
        if (id === MOBILE_ID) panel.classList.add('pt-popup-guard-mobile');
        state.restore.push(() => panel.classList.remove('pt-popup-guard-panel', 'pt-popup-guard-mobile'));
        mounted.set(panel, state);
      }
      const viewport = win.visualViewport;
      const height = viewport?.height || win.innerHeight;
      const keyboardGap = Math.max(0, doc.documentElement.clientHeight - (viewport?.offsetTop || 0) - height);
      const bottom = id === MOBILE_ID ? Math.max(state.bottom, keyboardGap) : 0;
      const values = { '--pt-popup-height': `${Math.max(1, height - (id === MOBILE_ID ? state.bottom : 0))}px`, '--pt-popup-bottom': `${bottom}px` };
      for (const [property, value] of Object.entries(values)) {
        if (!state.variables.has(property)) state.variables.set(property, {
          before: panel.style.getPropertyValue(property), priority: panel.style.getPropertyPriority(property), owned: value,
        });
        state.variables.get(property).owned = value;
        if (panel.style.getPropertyValue(property) !== value) panel.style.setProperty(property, value);
      }
      if (suspend && !state.suspended) {
        if (panel.contains(doc.activeElement)) focusedField = doc.activeElement;
        state.suspended = suspendPanel(panel);
      } else if (suspend && state.suspended) {
        state.suspended.enforce();
      } else if (!suspend && state.suspended) {
        state.suspended();
        state.suspended = null;
      }
    }
    if (consentPanel !== consent) {
      restoreConsentTabindex?.();
      restoreConsentTabindex = null;
    }
    if (suspend && focusedField && consentPanel !== consent) {
      // Move focus off a now-inert signup only. Do not interrupt ordinary page use.
      if (!consent.hasAttribute('tabindex')) restoreConsentTabindex = ownAttribute(consent, 'tabindex', '-1');
      consent.focus({ preventScroll: true });
    } else if (!suspend && focusedField) {
      if ((doc.activeElement === doc.body || consentPanel?.contains(doc.activeElement)) && focusStillAvailable(focusedField)) {
        focusedField.focus({ preventScroll: true });
      }
      focusedField = null;
    }
    consentPanel = consent || null;
  }

  function schedule() {
    if (!stopped && !frame) frame = win.requestAnimationFrame(sync);
  }
  function notice(panel, message) {
    let node = panel.querySelector('.pt-popup-verification-notice');
    if (!node) {
      const button = panel.querySelector('button[type="submit"]');
      const container = button?.closest('[class*="-action-container-"]');

      node = doc.createElement('p');
      node.className = 'pt-popup-verification-notice';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      if (container) container.after(node);
      else (button?.parentElement || panel).appendChild(node);
      notices.add(node);
    }
    node.textContent = message;
  }
  function beforeSubmit(event) {
    const button = event.target.closest?.('button[type="submit"]');
    const form = event.type === 'submit' ? event.target : button?.closest('form');
    if (!form) return;
    const entry = forms().find(({ panel }) => panel.contains(form));
    if (!entry) return;
    const result = validateSubmission(Array.from(form.querySelectorAll('input[required], select[required], textarea[required]')),
      typeof win.grecaptcha?.execute === 'function');
    if (result.kind === 'ready') {
      submitting = entry;
      entry.panel.querySelector('.pt-popup-verification-notice')?.remove();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (result.kind === 'blank-name') {
      notice(entry.panel, 'Please enter your first name.');
      result.field.focus();
    } else if (result.kind === 'verification-unavailable') {
      notice(entry.panel, 'Verification is still loading. Please wait a moment, then try again.');
    }
  }
  const observer = new win.MutationObserver(schedule);
  observer.observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'inert', 'aria-hidden'] });
  const events = [[doc, 'click', beforeSubmit, true], [doc, 'submit', beforeSubmit, true],
    [doc, 'avada:consent-updated', schedule], [doc, 'transitionend', schedule, true],
    [doc, 'animationend', schedule, true], [win, 'resize', schedule], [win, 'pageshow', schedule]];
  if (win.visualViewport) events.push([win.visualViewport, 'resize', schedule], [win.visualViewport, 'scroll', schedule]);
  events.forEach(([target, type, handler, capture]) => target.addEventListener(type, handler, capture));
  sync();
  return () => {
    stopped = true;
    observer.disconnect();
    if (frame) win.cancelAnimationFrame(frame);
    challengeState?.release();
    restoreConsentTabindex?.();
    events.forEach(([target, type, handler, capture]) => target.removeEventListener(type, handler, capture));
    for (const [panel, state] of mounted) {
      releasePanel(panel, state);
    }
    notices.forEach(node => node.remove());
  };
}

if (typeof document !== 'undefined') {
  const key = Symbol.for('pixeltools.popup-guard');
  window[key]?.();
  let stop = null;
  const boot = () => {
    stop?.();
    stop = null;
    const setting = document.querySelector('[data-pt-popup-guard]');
    if (!setting || (setting.dataset.qaOnly === 'true' && !new URLSearchParams(location.search).get('pt_popup_qa')?.startsWith('v9'))) return;
    stop = startPopupGuard();
  };
  const unload = event => {
    if (event.target.querySelector('[data-pt-popup-guard]')) { stop?.(); stop = null; }
  };
  window[key] = () => {
    stop?.();
    document.removeEventListener('shopify:section:load', boot);
    document.removeEventListener('shopify:section:unload', unload);
  };
  boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', unload);
}
